import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { convertUnit, getDisplayUnitAndSymbol, Unit } from '@core';
import { useTranslate } from '@i18n';
import { useLiveAnalysis } from '@queries';
import { activityIdAtom, activityUnitAtom, parametersAtom, swingMeasuredParametersMapAtom } from '@store';

import { Tare } from './context';
import { useMergedCorridors } from './useMergedCorridors';

export const useLiveAnalysisTable = (isSetup?: boolean) => {
    const t = useTranslate();
    const activityId = useAtomValue(activityIdAtom);
    const activityUnit = useAtomValue(activityUnitAtom);
    const { data: liveEvents = [] } = useLiveAnalysis(activityId);

    const parameters = useAtomValue(parametersAtom);
    const corridors = useMergedCorridors();

    const supportedParameters = useMemo(
        () => parameters?.filter(({ parameter }) => parameter.supportedLive) ?? [],
        [parameters],
    );

    const supportedParametersMap = useMemo(
        () => Object.fromEntries(supportedParameters.map((node) => [node.parameter.smpId, node] as const)),
        [supportedParameters],
    );

    const liveParameterIds = useMemo(() => new Set(liveEvents.map((event) => event.parameterId)), [liveEvents]);

    const selectedParameters = useMemo(
        () =>
            supportedParameters.filter(({ parameter }) => {
                if (!liveParameterIds.has(parameter.smpId)) {
                    return false;
                }
                if (isSetup) {
                    return parameter.position === 'p1';
                } else {
                    return parameter.position !== 'p1';
                }
            }),
        [isSetup, liveParameterIds, supportedParameters],
    );

    const swingMeasuredParametersMap = useAtomValue(swingMeasuredParametersMapAtom);
    const selectedParameterColumns = useMemo(
        () =>
            selectedParameters
                .map((parameter) => {
                    const swingMeasuredParameter = swingMeasuredParametersMap[parameter.parameter.smpId];
                    const parameterCorridors = corridors[parameter.parameter.smpId];
                    if (!parameterCorridors) return null;

                    const { symbol } = getDisplayUnitAndSymbol(
                        swingMeasuredParameter.units?.notation as Unit,
                        activityUnit,
                    );
                    return {
                        id: parameter.parameter.name,
                        smpId: parameter.parameter.smpId,
                        label: t(parameter.i18n ?? '-'),
                        min: parameterCorridors.min,
                        max: parameterCorridors.max,
                        displaySymbol: symbol,
                        unit: swingMeasuredParameter.units?.notation ?? '',
                    };
                })
                .filter((p) => !!p),
        [activityUnit, corridors, selectedParameters, swingMeasuredParametersMap, t],
    );

    const receivedParametersData = useMemo(() => {
        return Object.values(
            liveEvents.reduce(
                (acc, event) => {
                    if (!acc[event.tareNum]) acc[event.tareNum] = {};

                    const node = supportedParametersMap[event.parameterId];
                    if (!node) return acc;

                    const tare = acc[event.tareNum];

                    const id = node.parameter.name;

                    const { value } = convertUnit({
                        unit: node.parameter.unit as Unit,
                        value: event.value,
                        userPreferredUnit: activityUnit,
                    });

                    if (!tare[id]) tare[id] = [];
                    tare[id].push(value);

                    return acc;
                },
                {} as Record<number, Tare>,
            ),
        );
    }, [activityUnit, liveEvents, supportedParametersMap]);

    const sum = useMemo(() => {
        return liveEvents.reduce((acc, event) => {
            const node = supportedParametersMap[event.parameterId];
            if (!node) return acc;

            const id = node.parameter.name;

            const { value } = convertUnit({
                unit: node.parameter.unit as Unit,
                value: event.value,
                userPreferredUnit: activityUnit,
            });

            if (!acc[id]) acc[id] = [];
            acc[id].push(value);

            return acc;
        }, {} as Tare);
    }, [activityUnit, liveEvents, supportedParametersMap]);

    return {
        parameters: selectedParameterColumns,
        data: receivedParametersData,
        sum,
    };
};
